import React, { useState, useEffect, useMemo } from "react";
import "../../ManagerDashboard.css";
import "./EventsPane.css";
import EventCard from "./EventCard";
import Joyride, { STATUS } from "react-joyride";
import {
  FaQuestionCircle,
  FaCalendarAlt,
  FaCalendarWeek,
} from "react-icons/fa";
import {
  convertFromFirestoreTimestamp,
  convertToFirestoreTimestamp,
} from "../../../../backend/timeFunctions";
// Event CRUD operations are now handled by parent component with real-time listeners
import { Button, Form, Spinner, Alert } from "react-bootstrap";
import { Timestamp } from "firebase/firestore";
import CustomModal from "../../../Global/Modal/CustomModal";
import { FaRegCalendarPlus } from "react-icons/fa";
import DatePicker from "react-datepicker";
import "../../../../styles/react-datepicker.css";
import { he } from "date-fns/locale";

const EventsPane = ({
  events,
  groups,
  students,
  evaluationTemplates,
  onEventUpdate,
  onEventAdd,
  onEventDelete,
  userID,
  activateAlert,
  userData,
}) => {

  const [showEventFormModal, setShowEventFormModal] = useState(false);
  const [eventName, setEventName] = useState("");
  const [eventStartTime, setEventStartTime] = useState(null); // Use Date object
  const [eventEndTime, setEventEndTime] = useState(null); // Use Date object
  const [eventDescription, setEventDescription] = useState("");
  const [eventLocation, setEventLocation] = useState("");
  const [eventID, setEventID] = useState("");
  const [eventPaymentUrl, setEventPaymentUrl] = useState("");
  const [isEditedEvent, setIsEditedEvent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingEventId, setDeletingEventId] = useState(null);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [selectedEvaluationTemplate, setSelectedEvaluationTemplate] =
    useState("");
  const [initialFormState, setInitialFormState] = useState(null);
  const [warningMessage, setWarningMessage] = useState("");
  const [runTour, setRunTour] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showNoGroupsConfirmation, setShowNoGroupsConfirmation] = useState(false);
  const [pendingSubmission, setPendingSubmission] = useState(null);

  // Helper function to truncate text with ellipsis
  const truncateText = (text, maxLength = 30) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  // Helper function to validate if a template ID exists in the available templates
  const isValidTemplateId = (templateId) => {
    if (
      !templateId ||
      !evaluationTemplates ||
      !Array.isArray(evaluationTemplates)
    ) {
      return false;
    }
    return evaluationTemplates.some(
      (template) => template && template.templateId === templateId
    );
  };

  // Effect to validate selected template when evaluationTemplates change
  useEffect(() => {
    if (
      selectedEvaluationTemplate &&
      !isValidTemplateId(selectedEvaluationTemplate)
    ) {
      // If the selected template is no longer valid, clear the selection
      setSelectedEvaluationTemplate("");
      if (initialFormState) {
        // Update initial form state to reflect the change
        setInitialFormState({
          ...initialFormState,
          evaluationTemplate: "",
        });
      }
    }
  }, [evaluationTemplates, selectedEvaluationTemplate, initialFormState]);

  // Ensure evaluationTemplates is always an array
  const safeEvaluationTemplates = Array.isArray(evaluationTemplates)
    ? evaluationTemplates
    : [];

  // Combine real groups and sort by dojo, then by name
  const allGroups = useMemo(() => {
    const realGroups = groups || [];

    // Sort by dojo first, then by group name
    return realGroups.sort((a, b) => {
      // First sort by dojo
      if (a.dojo < b.dojo) return -1;
      if (a.dojo > b.dojo) return 1;

      // If same dojo, sort by group name
      if (a.name < b.name) return -1;
      if (a.name > b.name) return 1;

      return 0;
    });
  }, [groups]);

  // Filter groups based on search query
  const filteredGroups = useMemo(() => {
    if (!searchQuery.trim()) return allGroups;

    const query = searchQuery.toLowerCase();
    return allGroups.filter(group =>
      group.name.toLowerCase().includes(query) ||
      group.dojo.toLowerCase().includes(query)
    );
  }, [allGroups, searchQuery]);

  // Check if user has view privilege - default to true if coachData is missing
  const hasViewPrivilege =
    userData?.userType === "manager" ||
    userData?.coachData?.accessPrivilege?.events?.view ||
    (userData?.userType === "coach" && !userData?.coachData) || // Default allow if coachData is missing
    false;
  const hasEditPrivilege =
    userData?.userType === "manager" ||
    userData?.coachData?.accessPrivilege?.events?.write ||
    (userData?.userType === "coach" && !userData?.coachData) || // Default allow if coachData is missing
    false;

  // If user doesn't have view privilege, show message and return
  if (!hasViewPrivilege) {
    return (
      <div className="events-wrapper">
        <Alert variant="warning" className="m-3" style={{ direction: "rtl" }}>
          אין לך הרשאות לצפייה באירועים. אנא פנה למנהל המערכת לקבלת הרשאות.
        </Alert>
      </div>
    );
  }

  // Define Joyride steps for the new design
  const steps = [
    {
      target: ".events-main-container",
      content:
        "ברוכים הבאים למסך האירועים החדש! כאן תוכלו לנהל את כל האירועים בארגון בצורה מודרנית ונוחה.",
      placement: "center",
      disableBeacon: true,
    },
    {
      target: '[data-tour="add-event-button"]',
      content:
        "כאן תוכלו להוסיף אירוע חדש לארגון. לחצו על הכפתור והשלימו את הפרטים.",
      placement: "bottom",
    },
    // Future events section (conditionally rendered)
    ...(events &&
      events.some((event) => {
        const eventDate = event.date?.toDate?.()?.getTime() || 0;
        return eventDate > Date.now();
      })
      ? [
        {
          target: ".future-events-section-header",
          content:
            "כאן תמצאו את כל האירועים המתקרבים. לחצו על אירוע כדי לראות פרטים נוספים.",
          placement: "bottom",
        },
      ]
      : []),
    // Past events section (conditionally rendered)
    ...(events &&
      events.some((event) => {
        const eventDate = event.date?.toDate?.()?.getTime() || 0;
        return eventDate <= Date.now();
      })
      ? [
        {
          target: ".past-events-section-header",
          content:
            "כאן תמצאו את כל האירועים שהתרחשו בעבר. ניתן לעיין בפרטי האירועים הקודמים.",
          placement: "bottom",
        },
      ]
      : []),
    {
      target: '[data-tour="guide-button"]',
      content: "לחצו כאן בכל עת כדי להפעיל שוב את הסיור המודרך!",
      placement: "right",
      spotlightClicks: true,
    },
  ];

  // Handle tour events
  const handleJoyrideCallback = (data) => {
    const { status } = data;
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      setRunTour(false);
    }
  };

  // Add CSS for the tour button
  const tourButtonStyle = {
    position: "absolute",
    top: "20px",
    left: "20px",
    backgroundColor: "#0066CC",
    color: "white",
    border: "none",
    borderRadius: "50%",
    width: "40px",
    height: "40px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: "all 0.3s ease",
    zIndex: 100,
    boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
  };

  const handleTourButtonClick = () => {
    setRunTour(true);
  };

  const openEventFormModal = () => {
    setWarningMessage("");
    setShowEventFormModal(true);
    // Set initial form state when opening modal for new event
    setInitialFormState({
      name: eventName,
      startTime: eventStartTime,
      endTime: eventEndTime,
      description: eventDescription,
      location: eventLocation,
      groups: [...selectedGroups],
      evaluationTemplate: selectedEvaluationTemplate,
    });
    // Remove logic that sets end time to start time by default
  };

  const openEventEditModal = (event) => {
    setEventName(event.name);
    setEventID(event.id);
    setEventLocation(event.location);
    setEventStartTime(event.date?.toDate ? event.date.toDate() : null);
    setEventEndTime(event.endDate?.toDate ? event.endDate.toDate() : null);
    setEventDescription(event.details);
    setEventPaymentUrl(event.paymentUrl || "");
    setSelectedGroups(event.groupsInEvent || []);

    // Handle evaluation template ID validation
    const templateId = event.evaluationTemplateId;
    if (templateId && evaluationTemplates && evaluationTemplates.length > 0) {
      // Check if the template ID exists in the available templates
      const templateExists = evaluationTemplates.some(
        (template) => template.templateId === templateId
      );
      setSelectedEvaluationTemplate(templateExists ? templateId : "");
    } else {
      setSelectedEvaluationTemplate("");
    }

    setIsEditedEvent(true);
    setShowEventFormModal(true);
    // Set initial form state when opening modal for edit
    setInitialFormState({
      name: event.name,
      startTime: event.date?.toDate ? event.date.toDate() : null,
      endTime: event.endDate?.toDate ? event.endDate.toDate() : null,
      description: event.details,
      location: event.location,
      paymentUrl: event.paymentUrl || "",
      groups: event.groupsInEvent || [],
      evaluationTemplate:
        templateId &&
          evaluationTemplates &&
          evaluationTemplates.length > 0 &&
          evaluationTemplates.some(
            (template) => template.templateId === templateId
          )
          ? templateId
          : "",
    });
  };

  const hasUnsavedChanges = () => {
    if (!initialFormState) return false;

    // For new event, check if any field has been filled
    if (!isEditedEvent) {
      return (
        eventName !== "" ||
        eventStartTime !== null ||
        eventEndTime !== null ||
        eventDescription !== "" ||
        eventLocation !== "" ||
        eventPaymentUrl !== "" ||
        selectedGroups.length > 0 ||
        selectedEvaluationTemplate !== ""
      );
    }

    // For edit event, compare with initial state
    // Helper function to safely compare dates
    const compareDates = (date1, date2) => {
      if (!date1 && !date2) return true; // Both null/undefined
      if (!date1 || !date2) return false; // One is null/undefined, other isn't
      if (typeof date1 === "string" && typeof date2 === "string") {
        return date1 === date2;
      }
      if (date1 instanceof Date && date2 instanceof Date) {
        return date1.getTime() === date2.getTime();
      }
      return false; // Different types
    };

    return (
      initialFormState.name !== eventName ||
      !compareDates(initialFormState.startTime, eventStartTime) ||
      !compareDates(initialFormState.endTime, eventEndTime) ||
      initialFormState.description !== eventDescription ||
      initialFormState.location !== eventLocation ||
      initialFormState.paymentUrl !== eventPaymentUrl ||
      JSON.stringify(initialFormState.groups) !==
      JSON.stringify(selectedGroups) ||
      initialFormState.evaluationTemplate !== selectedEvaluationTemplate
    );
  };

  const resetFormState = () => {
    setShowEventFormModal(false);
    setEventName("");
    setEventStartTime(null);
    setEventEndTime(null);
    setEventDescription("");
    setEventLocation("");
    setEventPaymentUrl("");
    setEventID("");
    setSelectedGroups([]);
    setSelectedEvaluationTemplate("");
    setIsEditedEvent(false);
    setInitialFormState(null);
  };

  const handleModalHide = () => {
    // If we're submitting, just close the modal without any checks
    if (isSubmitting) {
      resetFormState();
      return;
    }

    // Only check for unsaved changes if we're not submitting
    if (hasUnsavedChanges()) {
      if (
        window.confirm("יש לך שינויים שלא נשמרו. האם אתה בטוח שברצונך לצאת?")
      ) {
        resetFormState();
      } else {
        return false;
      }
    } else {
      resetFormState();
    }
  };

  const closeEventModal = () => {
    // If we're submitting, just close the modal without any checks
    if (isSubmitting) {
      resetFormState();
      return;
    }

    // Only check for unsaved changes if we're not submitting
    if (hasUnsavedChanges()) {
      if (
        window.confirm("יש לך שינויים שלא נשמרו. האם אתה בטוח שברצונך לצאת?")
      ) {
        resetFormState();
      } else {
        return;
      }
    } else {
      resetFormState();
    }
  };

  const handleEventNameChange = (event) => {
    setEventName(event.target.value);
  };

  const handleEventStartTimeChange = (date) => {
    setEventStartTime(date);
    // If the end time is before the new start time, clear the end time
    if (eventEndTime && date && eventEndTime < date) {
      setEventEndTime(null);
    }
  };

  const handleEventEndTimeChange = (date) => {
    // Only allow end time if it's not before start time
    if (eventStartTime && date && date < eventStartTime) {
      setEventEndTime(null);
      return;
    }
    setEventEndTime(date);
  };

  const handleEventDescriptionChange = (event) => {
    setEventDescription(event.target.value);
  };

  const handleEventLocationChange = (event) => {
    setEventLocation(event.target.value);
  };

  const handleEventPaymentUrlChange = (event) => {
    setEventPaymentUrl(event.target.value);
  };

  const handleEvaluationTemplateChange = (event) => {
    setSelectedEvaluationTemplate(event.target.value);
  };

  const handleNewEventSubmit = async () => {
    // Reset warning message
    setWarningMessage("");

    // Validate required fields
    if (!eventName.trim()) {
      setWarningMessage("נא להזין שם לאירוע");
      return;
    }

    if (!eventStartTime) {
      setWarningMessage("נא להזין זמן התחלה לאירוע");
      return;
    }

    // Check if no groups are selected and there are existing groups
    if (selectedGroups.length === 0 && allGroups.length > 0) {
      setPendingSubmission("new");
      setShowNoGroupsConfirmation(true);
      return;
    }

    // If validation passes and groups are selected (or no groups exist), proceed with submission
    await handleNewEventSubmitConfirmed();
  };

  const handleEditedEventSubmit = async () => {
    // Reset warning message
    setWarningMessage("");

    // Validate required fields
    if (!eventName.trim()) {
      setWarningMessage("נא להזין שם לאירוע");
      return;
    }

    if (!eventStartTime) {
      setWarningMessage("נא להזין זמן התחלה לאירוע");
      return;
    }

    // Check if no groups are selected and there are existing groups
    if (selectedGroups.length === 0 && allGroups.length > 0) {
      setPendingSubmission("edit");
      setShowNoGroupsConfirmation(true);
      return;
    }

    // If validation passes and groups are selected (or no groups exist), proceed with submission
    await handleEditedEventSubmitConfirmed();
  };

  const handleEventDelete = async (event) => {
    setDeletingEventId(event.id);
    try {
      // Use parent component handler which will update database and real-time listener will handle state
      await onEventDelete(event.id);
      activateAlert("success", "אליפות!", "האירוע נמחק בהצלחה");
    } catch (error) {
      console.error("Failed to delete event:", error);
      activateAlert("error", "אופס", "לא הצלחנו למחוק את האירוע");
    } finally {
      setDeletingEventId(null);
    }
  };

  // Handle confirmation modal actions
  const handleConfirmSubmit = async () => {
    setShowNoGroupsConfirmation(false);

    if (pendingSubmission === "new") {
      await handleNewEventSubmitConfirmed();
    } else if (pendingSubmission === "edit") {
      await handleEditedEventSubmitConfirmed();
    }

    setPendingSubmission(null);
  };

  const handleCancelSubmit = () => {
    setShowNoGroupsConfirmation(false);
    setPendingSubmission(null);
  };

  const handleNewEventSubmitConfirmed = async () => {
    setIsSubmitting(true);
    // Create the new event object, create appropriate time stamps
    let startDateTimestamp = convertToFirestoreTimestamp(eventStartTime);
    let endTimeTimestamp = convertToFirestoreTimestamp(eventEndTime);

    const newEvent = {
      date:
        eventStartTime && startDateTimestamp
          ? new Timestamp(
            startDateTimestamp?.seconds,
            startDateTimestamp?.nanoseconds
          )
          : null,
      details: eventDescription,
      endDate:
        eventEndTime && endTimeTimestamp
          ? new Timestamp(
            endTimeTimestamp?.seconds,
            endTimeTimestamp?.nanoseconds
          )
          : null,
      groupsInEvent: selectedGroups,
      location: eventLocation,
      name: eventName,
      paymentUrl: eventPaymentUrl || null,
      evaluationTemplateId: isValidTemplateId(selectedEvaluationTemplate)
        ? selectedEvaluationTemplate
        : null,
    };

    try {
      // Use parent component handler which will update database and real-time listener will handle state
      await onEventAdd(newEvent);
      activateAlert("success", "אליפות!", "האירוע נוצר בהצלחה");
      resetFormState(); // Use resetFormState instead of closeEventModal
    } catch (error) {
      console.error("Failed to create event:", error);
      activateAlert("error", "אופס", "התרחשה תקלה ביצירת האירוע");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditedEventSubmitConfirmed = async () => {
    setIsSubmitting(true);
    // Create the new event object, create appropriate time stamps
    let startDateTimestamp = convertToFirestoreTimestamp(eventStartTime);
    let endTimeTimestamp = convertToFirestoreTimestamp(eventEndTime);

    const updatedEvent = {
      date: eventStartTime
        ? new Timestamp(
          startDateTimestamp?.seconds,
          startDateTimestamp?.nanoseconds
        )
        : null,
      details: eventDescription,
      id: eventID,
      endDate: eventEndTime
        ? new Timestamp(
          endTimeTimestamp?.seconds,
          endTimeTimestamp?.nanoseconds
        )
        : null,
      groupsInEvent: selectedGroups,
      isInvitationSent: null,
      location: eventLocation,
      name: eventName,
      paymentUrl: eventPaymentUrl || null,
      evaluationTemplateId: isValidTemplateId(selectedEvaluationTemplate)
        ? selectedEvaluationTemplate
        : null,
    };

    try {
      // Use parent component handler which will update database and real-time listener will handle state
      await onEventUpdate(updatedEvent);
      activateAlert("success", "אליפות!", "האירוע התעדכן בהצלחה");
      resetFormState(); // Use resetFormState instead of closeEventModal
    } catch (error) {
      console.error("Failed to edit event:", error);
      activateAlert("error", "אופס", "התרחשה תקלה בעדכון האירוע");
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- DESIGN STYLES ---
  const mainBg = {
    minHeight: "100vh",
    background: "linear-gradient(135deg, #f8fafc 0%, #e3e9f7 100%)",
    padding: "32px 0",
  };
  const containerStyle = {
    maxWidth: 1200,
    margin: "0 auto",
    padding: "0 16px",
  };
  const headerStyle = {
    fontWeight: 800,
    fontSize: "2.5rem",
    letterSpacing: 1,
    marginBottom: 24,
    textAlign: "center",
  };
  const sectionCardStyle = (color) => ({
    marginBottom: 40,
    background: "#fff",
    borderRadius: 18,
    boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
    padding: 24,
    direction: "rtl",
  });
  const sectionHeaderStyle = (color) => ({
    fontWeight: 700,
    fontSize: "1.4rem",
    color,
    borderRight: `6px solid ${color}`,
    paddingRight: 16,
    marginBottom: 24,
    display: "flex",
    alignItems: "center",
    gap: 10,
  });
  // Change from grid to flex column for event list
  const eventListStyle = {
    display: "flex",
    flexDirection: "column",
    gap: 24,
    marginBottom: 8,
  };
  const addButtonStyle = {
    fontWeight: 700,
    fontSize: "1.2rem",
    borderRadius: 999,
    padding: "16px 40px",
    boxShadow: "0 2px 8px rgba(0,180,80,0.10)",
    marginBottom: 32,
  };

  // --- END DESIGN STYLES ---

  // Helper to filter events into future and past
  const futureEvents = events
    ?.filter((event) => {
      const eventDate = event.date?.toDate?.()?.getTime() || 0;
      return eventDate > Date.now() && event.name && event.name.trim() !== "";
    })
    .sort((a, b) => {
      const dateA = a.date?.toDate?.()?.getTime() || 0;
      const dateB = b.date?.toDate?.()?.getTime() || 0;
      return dateA - dateB;
    });

  const pastEvents = events
    ?.filter((event) => {
      const eventDate = event.date?.toDate?.()?.getTime() || 0;
      return eventDate <= Date.now() && event.name && event.name.trim() !== "";
    })
    .sort((a, b) => {
      const dateA = a.date?.toDate?.()?.getTime() || 0;
      const dateB = b.date?.toDate?.()?.getTime() || 0;
      return dateB - dateA;
    });

  const hasAnyEvents = events && events.length > 0;
  const hasFutureEvents = futureEvents && futureEvents.length > 0;
  const hasPastEvents = pastEvents && pastEvents.length > 0;

  return (
    <div style={mainBg}>
      <style>{`
        .modern-event-card {
          border-radius: 16px !important;
          box-shadow: 0 2px 12px rgba(0,0,0,0.06) !important;
          background: #fff !important;
          transition: box-shadow 0.2s, transform 0.2s;
        }
        .modern-event-card:hover {
          box-shadow: 0 4px 24px #1976d233 !important;
          transform: translateY(-2px) scale(1.01);
        }
        .modern-section-header-icon {
          font-size: 1.3em;
          margin-left: 8px;
        }
        .modern-add-btn {
          border-radius: 999px !important;
          font-weight: 700 !important;
          font-size: 1.2rem !important;
          padding: 16px 40px !important;
          box-shadow: 0 2px 8px rgba(0,180,80,0.10) !important;
        }
        .modern-modal .modal-content {
          border-radius: 18px !important;
          background: #f8fafc !important;
        }
        .modern-modal .modal-header {
          border-radius: 18px 18px 0 0 !important;
        }
        .modern-modal .modal-footer {
          border-radius: 0 0 18px 18px !important;
        }
        .modern-form-input, .modern-form-input:focus {
          border-radius: 10px !important;
          box-shadow: 0 1px 4px rgba(0,0,0,0.04) !important;
          border: 1px solid #e0e0e0 !important;
          background: #fff !important;
        }
        .react-datepicker {
          direction: rtl;
          text-align: right;
        }
        .react-datepicker__header {
          direction: rtl !important;
          text-align: center !important;
        }
        .react-datepicker__current-month {
          direction: rtl !important;
          text-align: center !important;
          unicode-bidi: plaintext !important;
          width: 100%;
          display: block;
          font-weight: bold;
        }
        /* Move the clear (X) button to the left in RTL */
        .react-datepicker__close-icon {
          right: auto !important;
          left: 8px !important;
        }
        /* Add padding to the left of the input so text doesn't overlap the X */
        .react-datepicker__input-container input {
          padding-left: 32px !important;
          padding-right: 8px !important;
        }
        /* Hide day name labels */
        .react-datepicker__day-names {
          display: none !important;
        }
      `}</style>
      <div style={containerStyle} className="events-main-container">
        {/* Help (tour) button restored */}
        <button
          style={tourButtonStyle}
          onClick={handleTourButtonClick}
          title="הפעל סיור מודרך"
          data-tour="guide-button"
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "scale(1.1)";
            e.currentTarget.style.boxShadow = "0 4px 8px rgba(0,0,0,0.3)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "scale(1)";
            e.currentTarget.style.boxShadow = "0 2px 4px rgba(0,0,0,0.2)";
          }}
        >
          <FaQuestionCircle size={20} />
        </button>
        <h1 style={headerStyle}>אירועים</h1>
        {hasEditPrivilege && (
          <div style={{ textAlign: "center" }}>
            <Button
              variant="success"
              className="modern-add-btn"
              style={addButtonStyle}
              onClick={openEventFormModal}
              data-tour="add-event-button"
            >
              הוסף אירוע חדש
            </Button>
          </div>
        )}
        {/* Empty state if no events at all */}
        {!hasAnyEvents && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 320,
              color: "#888",
              fontWeight: 600,
              fontSize: "1.3rem",
              background: "#fff",
              borderRadius: 18,
              boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
              margin: "48px auto 0 auto",
              maxWidth: 500,
              padding: 48,
              direction: "rtl",
            }}
          >
            <FaRegCalendarPlus
              size={64}
              style={{
                marginBottom: 24,
                color: "#1976d2",
                opacity: 0.7,
              }}
            />
            לחץ למעלה כדי ליצור את האירוע הראשון שלך!
          </div>
        )}
        {/* Upcoming Events Section */}
        {hasFutureEvents && (
          <div style={sectionCardStyle("#1976d2")}>
            <div
              style={sectionHeaderStyle("#1976d2")}
              className="future-events-section-header"
            >
              <FaCalendarAlt className="modern-section-header-icon" />
              אירועים מתקרבים
            </div>
            <div style={eventListStyle}>
              {futureEvents.map((event) => (
                <div key={event.id}>
                  <EventCard
                    event={event}
                    groups={groups}
                    userID={userID}
                    userData={userData}
                    students={students}
                    onEdit={hasEditPrivilege ? openEventEditModal : null}
                    onDelete={hasEditPrivilege ? handleEventDelete : null}
                    isDeleting={deletingEventId === event.id}
                    hasEditPrivilege={hasEditPrivilege}
                    onEventUpdate={onEventUpdate}
                    data-tour="event-card"
                  />
                </div>
              ))}
            </div>
          </div>
        )}
        {/* Past Events Section */}
        {hasPastEvents && (
          <div style={sectionCardStyle("#ab47bc")}>
            <div
              style={sectionHeaderStyle("#ab47bc")}
              className="past-events-section-header"
            >
              <FaCalendarWeek className="modern-section-header-icon" />
              אירועים מהעבר
            </div>
            <div style={eventListStyle}>
              {pastEvents.map((event) => (
                <div key={event.id}>
                  <EventCard
                    event={event}
                    groups={groups}
                    userData={userData}
                    students={students}
                    onEdit={hasEditPrivilege ? openEventEditModal : null}
                    onDelete={hasEditPrivilege ? handleEventDelete : null}
                    isDeleting={deletingEventId === event.id}
                    hasEditPrivilege={hasEditPrivilege}
                    onEventUpdate={onEventUpdate}
                    data-tour="event-card"
                  />
                </div>
              ))}
            </div>
          </div>
        )}
        {/* Modals and Joyride remain unchanged, just add modern-modal class if possible */}
      </div>
      <Joyride
        steps={steps}
        run={runTour}
        continuous
        showProgress
        showSkipButton
        disableScrolling={false}
        scrollToFirstStep={true}
        scrollOffset={100}
        callback={handleJoyrideCallback}
        styles={{
          options: {
            arrowColor: "#fff",
            backgroundColor: "#fff",
            overlayColor: "rgba(0, 0, 0, 0.5)",
            primaryColor: "#0066CC",
            textColor: "#333",
            zIndex: 1000,
          },
          tooltipContainer: {
            direction: "rtl",
            textAlign: "right",
          },
          buttonNext: {
            backgroundColor: "#0066CC",
            fontSize: 14,
            padding: "8px 16px",
          },
          buttonBack: {
            marginRight: 10,
            fontSize: 14,
            padding: "8px 16px",
          },
          buttonSkip: {
            color: "#666",
            fontSize: 14,
            padding: "8px 16px",
          },
        }}
        locale={{
          back: "הקודם",
          close: "סגור",
          last: "סיום",
          next: "הבא",
          skip: "דלג",
        }}
      />
      {/** Event Form Modal */}
      {hasEditPrivilege && (
        <CustomModal
          isOpen={showEventFormModal}
          onClose={handleModalHide}
          title={isEditedEvent ? "עריכת אירוע" : "הוסף אירוע חדש"}
          footerButtons={[
            {
              title: isSubmitting ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  {!isEditedEvent ? "יוצר..." : "מעדכן..."}
                </>
              ) : (
                "שמירה"
              ),
              onClick: !isEditedEvent
                ? handleNewEventSubmit
                : handleEditedEventSubmit,
              disabled: isSubmitting || !hasUnsavedChanges(),
              style: "primary",
            },
            {
              title: "ביטול",
              onClick: closeEventModal,
              disabled: isSubmitting,
              style: "grey",
            },
          ]}
          formwarningMessage={warningMessage}
        >
          <Form>
            <div className="row" style={{ justifyContent: "center" }}>
              <div className="col-md-4">
                <Form.Group
                  className="eventFormFieldContainer"
                  controlId="eventGroups"
                >
                  <Form.Label>קבוצות משתתפות</Form.Label>
                  <div className="group-selection-container">
                    <div className="group-selection-header">
                      <div className="selection-info">
                        <span className="selected-count">
                          {selectedGroups.length} קבוצות נבחרו
                        </span>
                        {searchQuery.trim() && (
                          <span className="search-results-count">
                            • {filteredGroups.length} מתוך {allGroups.length} קבוצות נמצאו
                          </span>
                        )}
                      </div>
                      <div className="filter-controls">
                        <input
                          type="text"
                          placeholder="חיפוש קבוצות..."
                          className="group-search-input"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="clear-selection-row">
                      {filteredGroups && filteredGroups.length > 0 && (
                        <button
                          type="button"
                          className="select-all-btn"
                          onClick={() => {
                            const allFilteredGroupIds = filteredGroups.map(group => group.id);
                            setSelectedGroups(allFilteredGroupIds);
                          }}
                        >
                          בחר הכל
                        </button>
                      )}
                      {selectedGroups.length > 0 && (
                        <button
                          type="button"
                          className="clear-selection-btn"
                          onClick={() => setSelectedGroups([])}
                        >
                          נקה בחירה
                        </button>
                      )}
                    </div>

                    <div className="groups-grid">
                      {filteredGroups && (() => {
                        // Group filtered groups by dojo
                        const groupedByDojo = filteredGroups.reduce((acc, group) => {
                          if (!acc[group.dojo]) {
                            acc[group.dojo] = [];
                          }
                          acc[group.dojo].push(group);
                          return acc;
                        }, {});

                        return Object.entries(groupedByDojo).map(([dojo, dojoGroups]) => (
                          <div key={dojo} className="dojo-section">
                            <div className="dojo-section-header" title={dojo}>
                              {truncateText(dojo, 35)} ({dojoGroups.length} קבוצות)
                            </div>
                            <div className="dojo-groups-grid">
                              {dojoGroups.map((group) => (
                                <div
                                  key={group.id}
                                  className={`group-card ${selectedGroups.includes(group.id) ? 'selected' : ''}`}
                                  onClick={() => {
                                    if (selectedGroups.includes(group.id)) {
                                      setSelectedGroups(
                                        selectedGroups.filter((id) => id !== group.id)
                                      );
                                    } else {
                                      setSelectedGroups([...selectedGroups, group.id]);
                                    }
                                  }}
                                >
                                  <div className="group-card-content">
                                    <div className="group-info">
                                      <h4 className="group-name" title={group.name}>{truncateText(group.name, 35)}</h4>
                                    </div>
                                    <div className="selection-indicator">
                                      {selectedGroups.includes(group.id) ? (
                                        <div className="check-icon">✓</div>
                                      ) : (
                                        <div className="uncheck-icon"></div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ));
                      })()}
                    </div>

                    {filteredGroups && filteredGroups.length === 0 && (
                      <div className="no-groups-message">
                        <p>
                          {searchQuery.trim()
                            ? `לא נמצאו קבוצות עבור "${searchQuery}"`
                            : "אין קבוצות זמינות"
                          }
                        </p>
                      </div>
                    )}
                  </div>
                </Form.Group>
              </div>

              <div className="col-md-3">
                <Form.Group
                  className="eventFormFieldContainer"
                  controlId="eventStartTime"
                >
                  <Form.Label>תאריך וזמן התחלת האירוע</Form.Label>
                  <DatePicker
                    selected={eventStartTime}
                    onChange={handleEventStartTimeChange}
                    showTimeSelect
                    timeFormat="HH:mm"
                    timeIntervals={15}
                    dateFormat="yyyy-MM-dd HH:mm"
                    timeCaption="שעה"
                    className="eventFormInput form-control"
                    placeholderText="בחר תאריך ושעה"
                    isClearable
                    autoComplete="off"
                    locale={he}
                  />
                </Form.Group>

                <Form.Group
                  className="eventFormFieldContainer"
                  controlId="eventTime"
                >
                  <Form.Label>תאריך וזמן סיום האירוע</Form.Label>
                  <DatePicker
                    selected={eventEndTime}
                    onChange={handleEventEndTimeChange}
                    showTimeSelect
                    timeFormat="HH:mm"
                    timeIntervals={15}
                    dateFormat="yyyy-MM-dd HH:mm"
                    timeCaption="שעה"
                    className="eventFormInput form-control"
                    placeholderText="בחר תאריך ושעה"
                    isClearable
                    style={{ direction: "rtl", textAlign: "center" }}
                    autoComplete="off"
                    minDate={eventStartTime}
                    selectsEnd
                    startDate={eventStartTime}
                    endDate={eventEndTime}
                    minTime={
                      eventEndTime &&
                        eventStartTime &&
                        eventEndTime.toDateString() ===
                        eventStartTime.toDateString()
                        ? new Date(
                          0,
                          0,
                          0,
                          eventStartTime.getHours(),
                          eventStartTime.getMinutes()
                        )
                        : new Date(0, 0, 0, 0, 0)
                    }
                    maxTime={new Date(0, 0, 0, 23, 59)}
                    disabled={!eventStartTime}
                    locale={he}
                  />
                </Form.Group>

                <Form.Group
                  className="eventFormFieldContainer"
                  controlId="eventDescription"
                >
                  <Form.Label>תיאור האירוע</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    className="eventFormInput"
                    value={eventDescription}
                    onChange={handleEventDescriptionChange}
                  />
                </Form.Group>
              </div>

              <div className="col-md-3">
                <Form.Group
                  className="eventFormFieldContainer"
                  controlId="eventName"
                >
                  <Form.Label>שם האירוע</Form.Label>
                  <Form.Control
                    type="text"
                    className="eventFormInput"
                    value={eventName}
                    onChange={handleEventNameChange}
                  />
                </Form.Group>

                <Form.Group
                  className="eventFormFieldContainer"
                  controlId="eventLocation"
                >
                  <Form.Label>מיקום האירוע</Form.Label>
                  <Form.Control
                    type="text"
                    className="eventFormInput"
                    value={eventLocation}
                    onChange={handleEventLocationChange}
                  />
                </Form.Group>

                <Form.Group
                  className="eventFormFieldContainer"
                  controlId="eventPaymentUrl"
                >
                  <Form.Label>קישור תשלום לאירוע (אופציונלי)</Form.Label>
                  <Form.Control
                    type="url"
                    className="eventFormInput"
                    value={eventPaymentUrl}
                    onChange={handleEventPaymentUrlChange}
                    placeholder="הדבק קישור"
                  />
                </Form.Group>

                <Form.Group
                  className="eventFormFieldContainer"
                  controlId="eventEvaluationTemplate"
                >
                  <Form.Label>תבנית משוב (אופציונלי)</Form.Label>
                  <Form.Select
                    className="eventFormInput"
                    value={selectedEvaluationTemplate}
                    onChange={handleEvaluationTemplateChange}
                  >
                    <option value="">בחר תבנית משוב</option>
                    {safeEvaluationTemplates.length > 0 ? (
                      safeEvaluationTemplates
                        .filter(
                          (template) =>
                            template &&
                            template.templateId &&
                            template.name &&
                            typeof template.templateId === "string" &&
                            typeof template.name === "string" &&
                            !template.isArchived // Only allow non-archived templates
                        )
                        .map((template) => (
                          <option
                            key={template.templateId}
                            value={template.templateId}
                          >
                            {template.name}
                          </option>
                        ))
                    ) : (
                      <option value="" disabled>
                        אין תבניות משוב זמינות
                      </option>
                    )}
                  </Form.Select>
                </Form.Group>
              </div>
            </div>
          </Form>
        </CustomModal>
      )}

      {/* Confirmation Modal for No Groups Selected */}
      <CustomModal
        isOpen={showNoGroupsConfirmation}
        onClose={handleCancelSubmit}
        title="אין קבוצות נבחרות"
        footerButtons={[
          {
            title: "צור בכל זאת",
            onClick: handleConfirmSubmit,
            style: "primary",
          },
          {
            title: "חזור לעריכה",
            onClick: handleCancelSubmit,
            style: "grey",
          },
        ]}
        width={30}
        height={30}
      >
        <div>
          <p style={{ direction: "rtl" }}>
            לא בחרת אף קבוצה לאירוע. בחירת קבוצות תאפשר לך לשלוח הזמנות בקלות בהמשך.
          </p>
        </div>
      </CustomModal>
    </div>
  );
};

export default EventsPane;
